import { useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo } from 'react';
import { collect } from 'collect.js';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import {
  BusinessType,
  LegalForm,
  OnboardingStep,
  PaymentGateway,
  PaymentGatewayVerificationStatus,
  PromoCodeTag,
  SubscriptionProductType,
} from '../../services/exports/Constants';
import InfoSubmissionStep from '../../components/verification/InfoSubmissionStep';
import OwnerDocumentStep from '../../components/verification/OwnerDocumentStep';
import BusinessDocumentStep from '../../components/verification/BusinessDocumentStep';
import TaxInfoStep from '../../components/verification/TaxInfoStep';
import BankInfoStep from '../../components/verification/BankInfoStep';
import PersonalInfoStep from '../../components/verification/PersonalInfoStep';
import OwnerPersonalInfoStep from '../../components/verification/OwnerPersonalInfoStep';
import BusinessInfoStep from '../../components/verification/BusinessInfoStep';
import BusinessDetailsStep from '../../components/verification/BusinessDetailsStep';
import StoreDetailsStep from '../../components/verification/StoreDetailsStep';
import PaymentInfoStep from '../../components/verification/PaymentInfoStep';
import ContractStep from '../../components/verification/ContractStep';
import ContractPaymentStep from '../../components/verification/ContractPaymentStep';
import { SubscriptionWrapper } from '../../components/verification/SubscriptionStep';
import SubscriptionStep from '../../components/verification/SubscriptionStepStrategy';
import ShippingInfoStep from '../../components/verification/ShippingInfoStep';
import DocumentsStep from '../../components/verification/DocumentsStep';
import useApiClient from '../api/useApiClient';
import { useGetDocumentSlotsQuery, useGetPromoCodesQuery } from '../../store/api-slices';
import PromoCodeStep from '../../components/verification/PromoCodeStep';
import LoadingScreen from '../../components/loading/LoadingScreen';
import useBusinessProfile from '../context/useBusinessProfile';
import usePermissions, { ManagerRole } from '../auth/usePermissions';
import { BusinessProfileReaderRoles } from '../../services/permissions/PermissionResolver';
import {
  getRemainingStepsCount,
  getRequiredPersonalFields,
  isOnboardingComplete,
  isStepFinished,
  resolveMerchantBillingForSteps,
  checkBankDetails as ruleCheckBankDetails,
  checkBusinessInfo as ruleCheckBusinessInfo,
  checkDeliveryStructure as ruleCheckDeliveryStructure,
  checkDocumentSlots as ruleCheckDocumentSlots,
  checkPersonalInfo as ruleCheckPersonalInfo,
  checkShippingInfo as ruleCheckShippingInfo,
  checkStoreDetails as ruleCheckStoreDetails,
  checkTaxDetails as ruleCheckTaxDetails,
  findLastActiveStep as ruleFindLastActiveStep,
  getProgress as ruleGetProgress,
  getRequiredSteps as ruleGetRequiredSteps,
  runsGermanKycSteps,
} from './verificationRules';
import MobileAppStep from '../../components/verification/MobileAppStep';
import { countMissingSlots } from '../../components/document-slots/documentFiles';
import DeliveryStructureStep from '../../components/verification/DeliveryStructureStep';

export default function useVerification() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Hooks:useVerification' });

  const { BusinessProfilesManager, MerchantProfileManager, PromoCodesManager } = useApiClient();
  const { business_profile, getValue } = useBusinessProfile();
  const { hasAnyRole } = usePermissions();
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  // A non-billing provider settles with its restaurants outside the platform,
  // so onboarding completes without the Subscription/PaymentInfo/ContractPayment
  // steps (mirrors the backend merchant_billing gates). Fails closed while the
  // flags payload is missing — see resolveMerchantBillingForSteps.
  const hasMerchantBilling = resolveMerchantBillingForSteps(serviceProvider);
  const { company } = useSelector((store) => store.currentCompany);
  const merchant = company?.merchant ?? {};
  const {
    payment_options,
    service_contract,
    has_master_subscription,
    has_mobile_app_subscription,
    onboarding_complete_at,
  } = merchant;

  // Several mounted components use this hook at once (sidebar, onboarding guide, route guard); the
  // shared query sends one request for all of them where a per-instance fetch sent one each.
  const { data: promoCodesPage } = useGetPromoCodesQuery(
    {
      apiManager: PromoCodesManager,
      companyId: company?.id,
      params: {
        filter: {
          active: true,
          tag: PromoCodeTag.Master,
        },
        page: 1,
      },
    },
    {
      skip: !company?.id || hasAnyRole([ManagerRole.StoreEmployee, ManagerRole.StoreOperator]),
      refetchOnMountOrArgChange: true,
    },
  );
  const promoCodes = promoCodesPage?.data ?? [];

  const hasPaymentOption = payment_options?.length > 0;
  // The restaurant's own provider decides, as on the backend: a global admin's provider switch in
  // the sidebar must not turn the German steps on or off (Bohdan).
  const hasKycSteps = runsGermanKycSteps(company, company?.service_provider ?? serviceProvider);

  // Which of the document slots the restaurant still has to fill, shared by every mounted consumer
  // like the promo codes above. A new legal form changes which slots are required, so it is part
  // of the key. Whoever may not read the business profile never asks, and the step stays open for
  // them. `currentData`, not `data`: right after a location switch `data` still holds the previous
  // location's slots, which could finish this location's step and stamp its onboarding complete.
  const { currentData: documentSlots } = useGetDocumentSlotsQuery(
    {
      apiManager: BusinessProfilesManager,
      companyId: company?.id,
      legalForm: getValue('legal_form') ?? null,
    },
    {
      skip: !company?.id || !business_profile?.id || !hasKycSteps || !hasAnyRole(BusinessProfileReaderRoles),
    },
  );
  // Saving the store details writes the restaurant's address, which store staff may not do
  // (the API grants company_profile::write to neither role). The step stays required — whoever
  // is signed in, onboarding is only complete once sales or an onboarding manager confirmed
  // it — but staff see it read-only and move on past it. A super admin is not store staff:
  // hasAnyRole() counts them in every role unless told not to.
  const canConfirmStoreDetails = !hasAnyRole([ManagerRole.StoreEmployee, ManagerRole.StoreOperator], false);
  const hasMobileAppSubscription =
    has_mobile_app_subscription || collect(merchant?.skipped_verification_steps).contains(OnboardingStep.MobileApp);
  const hasPromoCode =
    promoCodes?.length > 0 || collect(merchant?.skipped_verification_steps).contains(OnboardingStep.PromoCode);
  const hasSignedContract =
    !serviceProvider?.has_merchant_contracts ||
    (service_contract && service_contract.signed_at !== null) ||
    collect(merchant?.skipped_verification_steps).contains(OnboardingStep.Contract);
  // Once the contract has been signed (or the contract step skipped), the
  // merchant is locked out of navigating back through the stepper.
  const isContractLocked =
    Boolean(service_contract?.signed_at) ||
    collect(merchant?.skipped_verification_steps).contains(OnboardingStep.Contract);
  // Immediate contract payment is collected in its own step (before the promo
  // code step). It only applies once there is a contract flagged for immediate
  // payment, and is considered done once that flag clears (i.e. it was paid).
  const requiresContractPayment =
    hasMerchantBilling &&
    serviceProvider?.has_merchant_contracts &&
    Boolean(service_contract?.is_immediate_payment_required);
  const hasPaidContract =
    !requiresContractPayment || collect(merchant?.skipped_verification_steps).contains(OnboardingStep.ContractPayment);

  // No restaurant selected (a reseller browsing provider-wide) means no business profile: every
  // step reads as unfinished and nothing is written back.
  const isCompany = business_profile?.business_type === BusinessType.Company;
  const isIndividual = business_profile?.business_type === BusinessType.Individual;

  const hasStripePayments = useMemo(
    () => (company?.payment_gateways ?? [company?.payment_gateway]).includes(PaymentGateway.Stripe),
    [company?.payment_gateways, company?.payment_gateway],
  );

  const requiredPersonalFields = useMemo(
    () => collect(getRequiredPersonalFields(business_profile?.legal_country)),
    [business_profile?.legal_country],
  );

  const unorderedSteps = useMemo(
    () => [
      {
        index: 0.5,
        id: OnboardingStep.StoreDetails,
        is_required: hasKycSteps,
        is_finished: checkStoreDetails(),
        name: t(OnboardingStep.StoreDetails),
        render: (props) => <StoreDetailsStep {...props} canConfirm={canConfirmStoreDetails} />,
      },
      {
        index: 1,
        id: OnboardingStep.BusinessInfo,
        is_required: true,
        is_finished: checkBusinessInfo(),
        name: hasKycSteps ? t(OnboardingStep.BusinessInfo) : t('store_and_business_info'),
        render: (props) => (hasKycSteps ? <BusinessDetailsStep {...props} /> : <BusinessInfoStep {...props} />),
      },
      {
        index: 2,
        id: OnboardingStep.PersonalInfo,
        is_required: true,
        is_finished: checkPersonalInfo(),
        name: hasKycSteps
          ? t(getValue('legal_form') === LegalForm.Gmbh ? 'owner_personal_info_gmbh' : 'owner_personal_info')
          : t(OnboardingStep.PersonalInfo),
        render: (props) => {
          if (hasKycSteps) {
            return <OwnerPersonalInfoStep {...props} />;
          }

          const filtered = {
            ...props,
            moveBackwards: isCompany ? props.moveBackwards : null,
          };

          return <PersonalInfoStep {...filtered} />;
        },
      },
      {
        index: 3,
        id: OnboardingStep.ShippingInfo,
        is_required: true,
        is_finished: checkShippingInfo(),
        name: t(OnboardingStep.ShippingInfo),
        render: (props) => <ShippingInfoStep {...props} />,
      },
      {
        index: 4,
        id: OnboardingStep.BankInfo,
        is_required: true,
        is_finished: checkBankDetails(),
        name: t(OnboardingStep.BankInfo),
        render: (props) => <BankInfoStep {...props} />,
      },
      {
        index: 5,
        id: OnboardingStep.TaxInfo,
        is_required: true,
        is_skippable: true,
        is_finished: checkTaxDetails(),
        name: t(OnboardingStep.TaxInfo),
        render: (props) => <TaxInfoStep isSkippable {...props} />,
      },
      {
        index: 6,
        id: OnboardingStep.Subscription,
        is_required: hasMerchantBilling,
        is_finished: has_master_subscription,
        name: t(OnboardingStep.Subscription),
        render: (props) => (
          <SubscriptionWrapper
            renderSubscriptions={({ loading, subscriptions, subscriptionProducts }) => {
              if (loading) {
                return <LoadingScreen />;
              }

              const masterSubscriptionIndex = collect(subscriptions).search(
                (subscription) =>
                  !!collect(subscription.items).first(
                    (item) =>
                      item.product.type === SubscriptionProductType.Plan ||
                      item.product.type === SubscriptionProductType.LegacyPlan,
                  ),
              );
              const masterSubscription =
                masterSubscriptionIndex !== false ? subscriptions[masterSubscriptionIndex] : null;

              return (
                <SubscriptionStep
                  key="master-subscription"
                  data={masterSubscription}
                  subscriptionProducts={subscriptionProducts}
                  filterOtherProducts={(item) =>
                    item.type !== SubscriptionProductType.GoogleAdsPlan &&
                    item.type !== SubscriptionProductType.MobileAppPlan
                  }
                  {...props}
                />
              );
            }}
          />
        ),
      },
      {
        index: 7,
        id: OnboardingStep.MobileApp,
        is_required: serviceProvider?.is_master,
        is_finished: hasMobileAppSubscription || !!service_contract?.signed_at,
        name: t(OnboardingStep.MobileApp),
        render: (props) => (
          <SubscriptionWrapper
            renderSubscriptions={({ loading, subscriptions, subscriptionProducts }) => {
              if (loading) {
                return <LoadingScreen />;
              }

              const product = collect(subscriptionProducts).firstWhere('type', SubscriptionProductType.MobileAppPlan);
              const mobileAppSubscriptionIndex = collect(subscriptions).search(
                (subscription) =>
                  !!collect(subscription.items).first(
                    (item) => item.product.type === SubscriptionProductType.MobileAppPlan,
                  ),
              );
              const mobileAppSubscription =
                mobileAppSubscriptionIndex !== false ? subscriptions[mobileAppSubscriptionIndex] : null;

              return (
                product && (
                  <MobileAppStep
                    key="mobile-app-subscription"
                    data={mobileAppSubscription}
                    product={product}
                    {...props}
                  />
                )
              );
            }}
          />
        ),
      },
      {
        index: 7.5,
        id: OnboardingStep.DeliveryStructure,
        is_required: serviceProvider?.is_master,
        is_finished: checkDeliveryStructure(),
        name: t(OnboardingStep.DeliveryStructure),
        render: (props) => <DeliveryStructureStep {...props} />,
      },
      {
        index: 8,
        id: OnboardingStep.Contract,
        // Contracts are a billing artifact (setup fees, payment sessions) —
        // gated like the contract nav group and routes.
        is_required: Boolean(serviceProvider?.has_merchant_contracts) && hasMerchantBilling,
        is_skippable: true,
        is_finished: hasSignedContract,
        name: t(OnboardingStep.Contract),
        render: (props) => <ContractStep isSkippable {...props} />,
      },
      // In Germany the documents go in the six named slots of the Upload documents step instead.
      {
        index: 9,
        id: OnboardingStep.BusinessDocs,
        is_required: !hasKycSteps && hasStripePayments && isCompany,
        is_finished: merchant.business_document_uploaded,
        name: t(OnboardingStep.BusinessDocs),
        render: (props) => <BusinessDocumentStep {...props} />,
      },
      {
        index: 10,
        id: OnboardingStep.PersonalDocs,
        is_required: !hasKycSteps && hasStripePayments,
        is_finished: merchant.owner_document_uploaded,
        name: t(OnboardingStep.PersonalDocs),
        render: (props) => <OwnerDocumentStep {...props} />,
      },
      ...(company?.payment_gateways ?? []).map((gateway, gatewayIndex) => {
        const status = company?.[`${gateway}_verification_status`];

        return {
          // In Germany the documents come after contract payment (Artur), and the payment
          // provider's onboarding follows them, as it followed the old document steps: Stripe
          // asks for the owner's ID there, and a Stripe step left waiting for it would be the
          // first unfinished step, which the stepper does not let the owner get past.
          index: hasKycSteps ? 12.7 + gatewayIndex * 0.01 : 11 + gatewayIndex * 0.1,
          id: `${OnboardingStep.InfoSubmission}_${gateway}`,
          is_required: true,
          is_finished:
            status === PaymentGatewayVerificationStatus.Pending || status === PaymentGatewayVerificationStatus.Verified,
          name: t('confirm_info_gateway', {
            paymentGateway: gateway.charAt(0).toUpperCase() + gateway.slice(1),
          }),
          render: (props) => <InfoSubmissionStep {...props} paymentGateway={gateway} />,
        };
      }),
      {
        index: 12,
        id: OnboardingStep.PaymentInfo,
        is_required: hasMerchantBilling,
        is_finished: hasPaymentOption,
        name: t(OnboardingStep.PaymentInfo),
        render: (props) => <PaymentInfoStep {...props} />,
      },
      {
        index: 12.5,
        id: OnboardingStep.ContractPayment,
        is_required: requiresContractPayment,
        is_finished: hasPaidContract,
        name: t(OnboardingStep.ContractPayment),
        render: (props) => <ContractPaymentStep {...props} />,
      },
      {
        index: 12.6,
        id: OnboardingStep.Documents,
        is_required: hasKycSteps,
        is_finished: checkDocuments(),
        name: t(OnboardingStep.Documents),
        render: (props) => <DocumentsStep {...props} />,
      },
      {
        index: 13,
        id: OnboardingStep.PromoCode,
        is_required: true,
        is_skippable: true,
        is_finished: hasPromoCode,
        name: t(OnboardingStep.PromoCode),
        render: (props) => <PromoCodeStep isSkippable {...props} />,
      },
    ],
    [
      business_profile,
      company,
      merchant,
      serviceProvider?.has_merchant_contracts,
      serviceProvider?.is_master,
      hasMerchantBilling,
      hasPaymentOption,
      hasSignedContract,
      requiresContractPayment,
      hasPaidContract,
      service_contract,
      has_master_subscription,
      hasMobileAppSubscription,
      hasPromoCode,
      isCompany,
      hasStripePayments,
      canConfirmStoreDetails,
      hasKycSteps,
      documentSlots,
    ],
  );

  // The stepper and the first unfinished step follow the array, the next and previous steps the
  // index: keep them in one order now that a step can move (the payment provider, in Germany).
  const steps = useMemo(() => [...unorderedSteps].sort((a, b) => a.index - b.index), [unorderedSteps]);

  const isFinished = useCallback(
    (step) => isStepFinished(step, merchant?.skipped_verification_steps),
    [merchant?.skipped_verification_steps],
  );

  const isComplete = useMemo(
    () => isOnboardingComplete(steps, merchant?.skipped_verification_steps, onboarding_complete_at),
    [steps, merchant?.skipped_verification_steps, onboarding_complete_at],
  );

  const markVerificationComplete = async () =>
    MerchantProfileManager.update({
      onboarding_complete_at: moment().format(),
    });

  useEffect(() => {
    isComplete && !onboarding_complete_at && company?.id && markVerificationComplete();
  }, [isComplete]);

  function findLastActiveStep() {
    return ruleFindLastActiveStep(steps, merchant?.skipped_verification_steps);
  }

  function getRequiredSteps() {
    return ruleGetRequiredSteps(steps);
  }

  function getRemainingSteps() {
    return getRemainingStepsCount(steps, merchant?.skipped_verification_steps);
  }

  function getPrevStep(step) {
    return collect(steps).where('index', '<', step.index).where('is_required').last();
  }

  function getNextStep(step) {
    return collect(steps)
      .where('index', '>', step.index)
      .where('is_required')
      .filter((item) => !isFinished(item))
      .first();
  }

  function getStep(index) {
    return collect(steps).firstWhere('index', index);
  }

  function getProgress() {
    return ruleGetProgress(steps, merchant?.skipped_verification_steps);
  }

  function checkStoreDetails() {
    return ruleCheckStoreDetails(merchant);
  }

  function checkBusinessInfo() {
    return ruleCheckBusinessInfo(getValue, {
      ownProfile: company?.business_profile,
      requiresConfirmation: hasKycSteps && merchant.requires_kyc_confirmation === true,
    });
  }

  function checkPersonalInfo() {
    return ruleCheckPersonalInfo(getValue, business_profile?.legal_country, {
      ownProfile: company?.business_profile,
      requiresConfirmation: hasKycSteps && merchant.requires_kyc_confirmation === true,
    });
  }

  function checkShippingInfo() {
    return ruleCheckShippingInfo(getValue);
  }

  function checkBankDetails() {
    return ruleCheckBankDetails(getValue, {
      paymentGateway: company?.payment_gateway,
      stripeBankAccountId: merchant.stripe_bank_account_id,
    });
  }

  function checkTaxDetails() {
    return ruleCheckTaxDetails(getValue);
  }

  function checkDeliveryStructure() {
    return ruleCheckDeliveryStructure(service_contract?.delivery_modes);
  }

  function checkDocuments() {
    return ruleCheckDocumentSlots(documentSlots, merchant);
  }

  // Get Started stays reachable once onboarding is done, as the place to see and add documents,
  // for restaurants in Germany that finish onboarding from PROD-543 on (Nishita). Others keep the
  // old behaviour: it disappears, and My Store → Documents is where documents go.
  const showsGetStartedAfterOnboarding = hasKycSteps && merchant.shows_get_started_after_onboarding === true;

  // Required documents still to upload: what the sidebar counts once onboarding is done. A
  // restaurant past the old document steps owes none (no backfill).
  const missingDocuments = merchant.requires_document_slots === false ? 0 : countMissingSlots(documentSlots);

  return {
    steps: collect(steps),
    isComplete,
    showsGetStartedAfterOnboarding,
    missingDocuments,
    isContractLocked,
    isCompany,
    isIndividual,
    requiredPersonalFields,
    findLastActiveStep,
    getRequiredSteps,
    getRemainingSteps,
    getPrevStep,
    getNextStep,
    getStep,
    getProgress,
  };
}
